import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient, authApi } from '@/lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setAccessToken: (token) => {
        apiClient.setAccessToken(token);
        set({ accessToken: token });
      },

      login: async (email, password) => {
        const { user, tokens } = await authApi.login({ email, password });
        apiClient.setAccessToken(tokens.accessToken);
        set({
          user,
          accessToken: tokens.accessToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      register: async (email, password, name) => {
        const { user, tokens } = await authApi.register({ email, password, name });
        apiClient.setAccessToken(tokens.accessToken);
        set({
          user,
          accessToken: tokens.accessToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch {
          // Ignore logout errors
        }
        apiClient.setAccessToken(null);
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      checkAuth: async () => {
        const { accessToken } = get();
        
        if (!accessToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        apiClient.setAccessToken(accessToken);

        try {
          const { user } = await authApi.me();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch {
          // Token expired, try to refresh
          try {
            const { tokens } = await authApi.refresh();
            apiClient.setAccessToken(tokens.accessToken);
            const { user } = await authApi.me();
            set({
              user,
              accessToken: tokens.accessToken,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch {
            // Refresh failed, clear auth
            apiClient.setAccessToken(null);
            set({
              user: null,
              accessToken: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        }
      },
    }),
    {
      name: 'piano-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
      }),
    }
  )
);
